import API from "api/client";
import { envConfig } from "config";

const { profileAPI } = envConfig;

/**
 * @typedef { import('consts/UserPermissionTypes').UserPermissionType } UserPermissionType
 * @typedef { import('types').User } User
 * @typedef { import('types').UserWithPermissions } UserWithPermissions
 * @typedef { import('types').Account } Account
 * @typedef { import('types').UserPasswordUpdate } UserPasswordUpdate
 */

/**
 * @returns {Promise<User>}
 */
const getCurrentUser = () => {
  return API.get(`${profileAPI}`).then((res) => res.data);
};

/**
 * @returns {Promise<{}>}
 */
const getUserNotifications = () => {
  return API.get(`${profileAPI}notifications`).then((res) => res.data);
};

/**
 * Fetches the permissions for the current user.
 * See `getUserWithPermissions` for fetching the user with permissions in a single response.
 * @returns {Promise<{ permissions: UserPermissionType[] }>}
 */
const getPermissions = () => {
  return API.get(`${profileAPI}permissions`).then((res) => res.data);
};

/**
 * The current user's response already includes `permissions`, computed atomically
 * server-side. Kept as a separate export for compatibility with callers that fetch
 * the user and permissions together.
 * @returns {Promise<UserWithPermissions>}
 */
const getUserWithPermissions = () => getCurrentUser();

/**
 * Impersonates a user. The response includes the impersonated user's permissions,
 * computed atomically with the impersonation so there's no follow-up call that
 * could fail independently of the action itself.
 * @param req {object}
 * @param req.userId {int}
 * @returns {Promise<UserWithPermissions>}
 */
const impersonateUserWithPermissions = (req) => {
  return API.put(`${profileAPI}impersonate`, req).then((res) => res.data);
};

/**
 * Stops impersonating and restores the original user, with permissions included
 * in the same response.
 * @returns {Promise<UserWithPermissions>}
 */
const stopImpersonatingUserWithPermissions = () => {
  return API.put(`${profileAPI}stop-impersonating`).then((res) => res.data);
};

/**
 * Switches the current account. The response includes the switched-to account's
 * permissions, computed atomically with the switch so there's no follow-up call
 * that could fail independently of the switch itself.
 * @param {number | string} accountRefid
 * @returns {Promise<Account & { permissions: UserPermissionType[] }>}
 */
const switchUserAccount = (accountRefid) => {
  return API.put(`${profileAPI}switch-account`, { accountRefid }).then((res) => res.data);
};

/**
 * @param {object} req
 * @param {number} req.firstName
 * @param {number} req.lastName
 * @param {number} req.email
 * @param {number} req.locale
 * @returns {Promise<{}>}
 */
const updateUser = (req) => {
  return API.put(`${profileAPI}`, req).then((res) => res.data);
};

/**
 * The response includes the updated user's permissions in the same call.
 * @param {object} req
 * @param {number} req.firstName
 * @param {number} req.lastName
 * @param {number} req.email
 * @param {number} req.locale
 * @returns {Promise<UserWithPermissions>}
 */
const updateUserWithPermissions = (req) => updateUser(req);

/**
 * @param {Array} notifications list of notifications
 * @param {object} notifications[i] notification
 * @param {string|number|null} notifications[i].id
 * @param {string|number} notifications[i].accountRefid
 * @param {string|number} notifications[i].userRefid
 * @param {string} notifications[i].notificationTypeUri
 * @param {string} notifications[i].frequencyUri
 * @returns {Promise<{}>}
 */
const updateUserNotifications = (notifications) => {
  return API.put(`${profileAPI}notifications`, notifications).then((res) => res.data);
};

/**
 * @param {UserPasswordUpdate} request
 * @returns {Promise<void>}
 */
const updateUserPassword = (request) => {
  return API.put(`${profileAPI}password`, {
    currentPassword: request.currentPassword,
    newPassword: request.newPassword,
  }).then((res) => res.data);
};

/**
 * @returns {Promise<{}>}
 */
const updateUserTermsOfUse = () => {
  return API.put(`${profileAPI}accept-terms`).then((res) => res.data);
};

/**
 * Sign terms of use and update user. The response includes the user's
 * permissions in the same call.
 * @returns {Promise<UserWithPermissions>}
 */
const updateUserTermsOfUseWithPermissions = () => updateUserTermsOfUse();

/**
 * Get list of accounts a user is a member of.
 * Majority of the time a user is a member of just one.
 *
 * @returns {Promise<Account[]>}
 */
const getUserAccounts = () => {
  return API.get(`${profileAPI}accounts`).then((res) => res.data);
};

export default {
  get: getCurrentUser,
  getUserWithPermissions,
  getPermissions,
  getAccounts: getUserAccounts,
  getNotifications: getUserNotifications,
  impersonate: impersonateUserWithPermissions,
  stopImpersonating: stopImpersonatingUserWithPermissions,
  switchAccount: switchUserAccount,
  update: updateUserWithPermissions,
  updateNotifications: updateUserNotifications,
  updatePassword: updateUserPassword,
  updateTOU: updateUserTermsOfUseWithPermissions,
};
